import { useEffect, useState, type FormEvent } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Trans, useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/auth/auth-layout";
import { SocialButtons } from "@/components/auth/social-buttons";
import { PASSWORD_MIN, PasswordField } from "@/components/auth/password-field";
import { Button } from "@/components/ui/button";
import { Field, FormError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { api } from "@/lib/api";
import { authClient, guardClientConfigured } from "@/lib/auth-client";
import { authErrorMessage } from "@/lib/i18n-errors";

export const Route = createFileRoute("/_auth/signup")({
  component: SignupPage,
});

function SignupPage() {
  const { t } = useTranslation("auth");
  const { t: tErrors } = useTranslation("errors");
  const { redirect } = Route.useSearch();
  const config = useQuery(api.users.authConfig);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);

  // A deployment mistake that would otherwise look like a mystery: Guard
  // refuses sign-ups from a browser that never identified itself.
  useEffect(() => {
    if (config?.guard && !guardClientConfigured) {
      console.error(
        "Echoes Guard is on for this backend, but this build has no VITE_GUARD_PUBLIC_ID. Sign-ups will be refused until the client can identify itself.",
      );
    }
  }, [config?.guard]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < PASSWORD_MIN) {
      setPasswordError(t("password.tooShort", { min: PASSWORD_MIN }));
      return;
    }
    setPasswordError(null);
    setPending(true);
    const result = await authClient.signUp.email({
      name: name.trim(),
      email: email.trim(),
      password,
      callbackURL: redirect ?? "/",
    });
    if (result.error) {
      setError(authErrorMessage(result.error, tErrors));
      setPending(false);
      return;
    }
    // With verification on, signing up creates the account but no session,
    // so there is no redirect to wait for: the next step is in the inbox.
    if (config?.emailVerificationRequired) {
      setSentTo(email.trim());
      setPending(false);
    }
  }

  const loginLink = (
    <Link
      to="/login"
      search={{ redirect }}
      className="text-foreground underline decoration-border-strong underline-offset-4 hover:decoration-foreground"
    />
  );

  if (sentTo) {
    return (
      <AuthLayout
        title={t("signup.checkInbox")}
        description={t("signup.checkInboxBody", { email: sentTo })}
        footer={<Trans t={t} i18nKey="signup.hasAccount" components={{ a: loginLink }} />}
      >
        {null}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t("signup.title")}
      description={t("signup.description")}
      footer={<Trans t={t} i18nKey="signup.hasAccount" components={{ a: loginLink }} />}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={t("fields.name")} htmlFor="name">
          <Input
            id="name"
            autoComplete="name"
            autoFocus
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label={t("fields.email")} htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <PasswordField
          id="password"
          label={t("fields.password")}
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          hint={t("password.hint", { min: PASSWORD_MIN })}
          error={passwordError}
        />
        <FormError>{error}</FormError>
        <Button type="submit" disabled={pending}>
          {pending ? <Spinner /> : null}
          {t("signup.submit")}
        </Button>
      </form>
      <SocialButtons
        config={config}
        destination={redirect ?? "/"}
        onError={(failure) => setError(authErrorMessage(failure, tErrors))}
      />
    </AuthLayout>
  );
}
